import styled from "@emotion/styled";
import {
  BetaBanner,
  Button,
  CopyToClipboard,
  FlexRow,
  H2,
  IconConfetti,
  IconTwitter,
  Link,
  LoadingEllipses,
  Modal,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import { paths } from "utils/paths";
import { GenericImage } from "features/images";
import editProfileExample from "assets/images/edit-profile-example.jpg";
import { useUpdateGalleryStatus } from "../api/useUpdateGalleryStatus";

type Props = {
  artistId: string;
  isPublished: boolean;
};

function BetaProfilePublishMessage({ artistId, isPublished }: Props) {
  const { mutate, isLoading, isSuccess, isError, error } =
    useUpdateGalleryStatus();

  const tweetText = `I just published my new Artist Profile on @thehugxyz!`;
  const tweetURL = `thehug.xyz${paths.artistProfile(artistId)}`;
  const tweetLink = `https://twitter.com/intent/tweet?text=${tweetText}%0A${tweetURL}`;

  const publish = () => mutate({ artistId, betaProfile: "PUBLISHED" });

  const { isOpen, open, close } = Modal.useModalState();

  const { origin } = window.location;
  const profileUrl = `${origin}${paths.artistProfile(artistId)}`;
  // If we just completed the mutation, show the temporary success message. Otherwise, show return
  // null for all future renders.
  if (isPublished) {
    return isSuccess ? (
      <BetaBannerGreen>
        <FlexRow flexWrap="nowrap" alignItems="center" itemsFlex="1 1 auto">
          <SIconRaisedHands />
          <FlexRow
            justifyContent="space-between"
            itemsFlex="0 1 auto"
            gap="10px 1rem"
          >
            <Stack gap="xs">
              <H2 size="sm">Your new profile is published!</H2>
              <SText size="xs">
                Copy the link to start sharing it, and don&apos;t forget to tag
                us @thehugxyz when you do.
              </SText>
            </Stack>
            <FlexRow gap="10px">
              <CopyToClipboard
                size="xs"
                variant="primary"
                value={profileUrl}
                buttonText="Copy Profile URL"
              />
              <Link
                to={tweetLink}
                size="xs"
                variant="primary"
                style={{ textTransform: "none" }}
              >
                <IconTwitter /> Share via Tweet
              </Link>
            </FlexRow>
          </FlexRow>
        </FlexRow>
      </BetaBannerGreen>
    ) : null;
  }

  return (
    <>
      <BetaBanner>
        <FlexRow flexWrap="nowrap" alignItems="center" itemsFlex="1 1 auto">
          <SIconRaisedHands />
          <FlexRow justifyContent="space-between" gap="10px 1rem">
            <Stack gap="xs" style={{ flex: "1 1 auto" }}>
              <H2 size="sm">
                You&apos;re in! Time to customize your new Profile!
              </H2>
              <Text size="xs">
                Your new Profile is currently in draft mode. Publish to share it
                publicly. <Br />
                <SButton
                  size="xs"
                  variant="text"
                  onClick={open}
                  disabled={isLoading}
                >
                  How to customize your profile
                </SButton>
                .
              </Text>
              <Text>
                <strong>Note:</strong> Your new Profile and any changes made by
                then will be automatically published on May 1.
              </Text>
            </Stack>
            <FlexRow gap="10px" style={{ flex: "2 1 auto" }}>
              <Link
                to={paths.artistPortfolioEdit(artistId)}
                size="xxs"
                variant="primary"
              >
                Edit Portfolio
              </Link>
              <Button size="xxs" onClick={publish} disabled={isLoading}>
                {isLoading ? (
                  <LoadingEllipses text="Publishing" />
                ) : (
                  "Publish New Profile"
                )}
              </Button>
            </FlexRow>
          </FlexRow>
        </FlexRow>
        {isError && <UnexpectedError error={error} />}
      </BetaBanner>

      <Modal isOpen={isOpen} onClose={close}>
        <TwoCols>
          <SampleProfile
            src={editProfileExample}
            sizes="100vw, (min-width: 831px) 50vw, (min-width: 1440px) 600px"
            alt="Two laptop computers showing the featured and portfolio sections of the new artist profile"
          />
          <Stack gap="sm">
            <H2 size="lg">How to Customize your new&nbsp;Profile</H2>
            <Text size="xs">
              Welcome to the new home for your art! Click the Edit button and
              follow these steps to customize the different sections in your
              Profile.
            </Text>
            <Stack gap="xs">
              <Text size="xs">
                <strong>Build a visually stunning Portfolio</strong>
                <br />
                Our new Portfolio tab supports both image and video uploads. You
                can also create galleries to categorize your work&mdash;from
                works-in-progress to merchandise, or whatever you can dream of!
              </Text>
              <Text size="xs">
                <strong>Feature your best work up&nbsp;front</strong>
                <br />
                Your Featured tab is now your personal Artist Profile homepage.
                Select your best work to display to make a big first impression.
              </Text>
            </Stack>
            <Text size="xs">
              <strong>
                <em>Happy with how things look?</em>
              </strong>{" "}
              It&apos;s time to hit Publish and officially bring your new
              Profile into the world! Share it widely and don&apos;t forget to
              tag us @thehugxyz when you do.
            </Text>
            <Text size="xs">
              <em>
                P.S. You can continue to access the rest of your familiar
                favorites&mdash;
                <Link to={paths.artistUpdates(artistId)} onClick={close}>
                  Updates
                </Link>
                ,{" "}
                <Link to={paths.artistReviews(artistId)} onClick={close}>
                  Reviews
                </Link>
                , and{" "}
                <Link to={paths.artistAbout(artistId)} onClick={close}>
                  About
                </Link>
                &mdash;through their respective tabs.
              </em>
            </Text>
            <Link to={paths.artistPortfolioEdit(artistId)} variant="primary">
              Edit Portfolio
            </Link>
          </Stack>
        </TwoCols>
      </Modal>
    </>
  );
}

const TwoCols = styled.div(({ theme }) => ({
  [theme.breakpoints.desktop]: {
    display: "flex",
    gap: "2rem",
    alignItems: "center",
    "&>*": {
      flex: "1 0 45%",
    },
  },
}));

const SampleProfile = styled(GenericImage)(({ theme }) => ({
  borderRadius: theme.borderRadius.sm,
  overflow: "hidden",
  marginBottom: "1em",
  marginTop: 10,
  boxShadow: theme.boxShadow.light,
  [theme.breakpoints.desktop]: {
    margin: 0,
  },
}));

const SIconRaisedHands = styled(IconConfetti)(({ theme }) => ({
  display: "none",
  margin: "0 auto",
  color: theme.colors.fg,
  ".accent-1": {
    color: theme.colors.accent3,
  },
  ".accent-2": {
    color: theme.colors.accent1,
  },
  ".accent-3": {
    color: theme.colors.accent2,
  },
  [theme.breakpoints.xs]: {
    display: "block",
    width: 40,
    "&&": {
      flex: "0 0 40px",
    },
  },
  [theme.breakpoints.tablet]: {
    "&&": { flex: "0 0 60px" },
    width: 60,
  },
}));

const SButton = styled(Button)({
  fontSize: "inherit",
});

const Br = styled.br({
  "@media (max-width: 730px)": {
    display: "none",
  },
});
const SText = styled(Text)({
  textWrap: "balance",
});
const BetaBannerGreen = styled(BetaBanner)(({ theme }) => ({
  backgroundColor: theme.colors.successL,
}));
export { BetaProfilePublishMessage };
